import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmployeeAvatarWithTooltip } from "@/components/employee/EmployeeAvatarWithTooltip.tsx";
import { CreatedByWorkflowInline } from "@/components/workflows/CreatedByWorkflowInline.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import { CreationSourceTabs } from "@/components/filter/CreationSourceTabs.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { usePerformanceReviewsHook } from "@/hooks/use-performance-reviews.hook.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { PerformanceReviewEditPageStep } from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import {
  usePerformanceReviewCreate,
  usePerformanceReviewsSearch,
} from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCreationSource, ColumnSizes } from "@/types/common.types.ts";
import {
  ApiPerformanceReviewSearchItem,
  ApiPerformanceReviewsFilter,
} from "@/types/performance-review.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  LinkTo,
  ListItem,
  Page,
  PaginationBlock,
  ProgressBar,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce, useEffectOnce } from "react-use";

export const PerformanceReviewsIndex = () => {
  const { t } = useTranslation();
  const library = filtersLibrary();
  const { renderDate, formatPeriodDates } = useFormatter();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canIEdit } = usePermissions();
  const canCreateReview = canIEdit(ApiPermission.performance_reviews_create);
  const navigate = useNavigate();

  useTitle(t("performance_review:performance_reviews"));

  const {
    getPerformanceReviewStatusTag,
    handleDeleteReview,
    handleLaunchReview,
    handlePauseReview,
    handleCompleteReview,
    canLaunchReview,
    canPauseReview,
    canCompleteReview,
  } = usePerformanceReviewsHook();

  const {
    filter: { filter: filter },
  } = useAppFilter<ApiPerformanceReviewsFilter>({
    filters: [library.page_number, library.text],
    source: "Performance Reviews",
  });

  const [searchTerm, setSearchTerm] = useState(filter.text ?? "");

  const rawSource = searchParams.get("source");
  const creationSource: ApiCreationSource | null =
    rawSource === "all"
      ? null
      : Object.values(ApiCreationSource).includes(rawSource as ApiCreationSource)
        ? (rawSource as ApiCreationSource)
        : ApiCreationSource.manual;

  const handleCreationSourceChange = (value: ApiCreationSource | undefined) => {
    setSearchParams((params) => {
      params.set("source", value ?? "all");
      params.delete("page");
      return params;
    });
  };

  const requestFilter = useMemo<ApiPerformanceReviewsFilter>(
    () => ({ ...filter, creation_source: creationSource }),
    [filter, creationSource]
  );

  const { data: reviews, isLoading, isFetching } = usePerformanceReviewsSearch(requestFilter);
  const createReview = usePerformanceReviewCreate();

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set("search", searchTerm);
        else searchParams.delete("search");
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const columns = useMemo<ColumnDef<ApiPerformanceReviewSearchItem>[]>(() => {
    return [
      {
        header: t("common:title"),
        cell: (row) =>
          row.row.original.title ? (
            <LinkTo
              to={`/performance-reviews/${row.row.original.id}/view/summary`}
              variant={"secondary"}
            >
              <ListItem
                emoji={row.row.original.emoji}
                title={row.row.original.title}
                forceShowAvatar={true}
                truncate={true}
                behavior={"card"}
              />
            </LinkTo>
          ) : (
            <ListItem
              emoji={undefined}
              preTitle={t("common:untitled")}
              forceShowAvatar={true}
              truncate={true}
              behavior={"card"}
            />
          ),
        accessorKey: "title",
        meta: { disableRowClick: true },
        ...ColumnSizes.employee,
      },
      {
        header: t("common:status"),
        cell: (row) => (
          <Stack items={"start"}>{getPerformanceReviewStatusTag(row.row.original.status)}</Stack>
        ),
        accessorKey: "status",
        ...ColumnSizes.status,
      },
      {
        header: t("common:progress"),
        cell: (cell) => (
          <Stack className={"pr-6 pt-1 pb-1"}>
            <ProgressBar
              completed={cell.row.original.reviewer_stats.completed_count}
              total={cell.row.original.reviewer_stats.total_count}
              tooltip={t("performance_review:reviews_completed_of", {
                current: cell.row.original.reviewer_stats.completed_count,
                total: cell.row.original.reviewer_stats.total_count,
              })}
            />
          </Stack>
        ),
        accessorKey: "progress",
        ...ColumnSizes.middle,
      },

      {
        header: t("common:period"),
        cell: (row) => {
          if (row.row.original.date_starts_at && row.row.original.date_ends_at) {
            return (
              <TableCell variant={"secondary"}>
                {
                  formatPeriodDates({
                    dateStart: row.row.original.date_starts_at,
                    dateEnd: row.row.original.date_ends_at,
                  })?.title
                }
              </TableCell>
            );
          } else if (row.row.original.date_starts_at) {
            return (
              <TableCell variant={"secondary"}>
                {renderDate(row.row.original.date_starts_at)} -
              </TableCell>
            );
          } else if (row.row.original.date_ends_at) {
            return (
              <TableCell variant={"secondary"}>
                - {renderDate(row.row.original.date_ends_at)}
              </TableCell>
            );
          }

          return <TableCell variant={"secondary"}>-</TableCell>;
        },
        accessorKey: "start_date",
        ...ColumnSizes.middle,
      },
      {
        header: t("common:owner"),
        cell: (row) =>
          row.row.original.created_by_workflow ? (
            <CreatedByWorkflowInline
              workflow={row.row.original.created_by_workflow}
              author={row.row.original.owners?.[0]}
            />
          ) : (
            <EmployeeAvatarWithTooltip size={"sm"} employees={row.row.original.owners} enableLink />
          ),
        accessorKey: "owner",
        meta: { disableRowClick: true },
        ...ColumnSizes.narrow,
      },
    ];
  }, [reviews]);

  const renderRowAction = useCallback(
    (row: Row<ApiPerformanceReviewSearchItem>) => {
      const review = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuItem
            title={t("common:edit")}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
            to={`/performance-reviews/${review.id}/setup/${PerformanceReviewEditPageStep.details}`}
          />
          <DropdownMenuItem
            to={`/performance-reviews/${review.id}/view/summary`}
            title={t("performance_review:go_to_review")}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-arrow-up-right-from-square"} />}
          />
          {canLaunchReview(review.status) && (
            <DropdownMenuItem
              onClick={() => handleLaunchReview(review)}
              title={`${t("performance_review:launch_review")}...`}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-circle-play"} />}
            />
          )}
          {canPauseReview(review.status) && (
            <DropdownMenuItem
              onClick={() => handlePauseReview(review)}
              title={t("performance_review:pause_review")}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-circle-pause"} />}
            />
          )}
          {canCompleteReview(review.status) && (
            <DropdownMenuItem
              onClick={() => handleCompleteReview(review)}
              title={`${t("performance_review:complete_review")}...`}
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-flag-checkered"} />}
            />
          )}
          <DropdownMenuItem
            variant={"menuDanger"}
            onClick={() => handleDeleteReview(review)}
            title={`${t("common:delete")}...`}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          />
        </DropdownMenu>
      );
    },
    [
      canLaunchReview,
      canPauseReview,
      canCompleteReview,
      handleLaunchReview,
      handlePauseReview,
      handleCompleteReview,
      handleDeleteReview,
      t,
    ]
  );

  const handleReviewCreate = async () => {
    try {
      const data = await createReview.mutateAsync();
      analyticsService.trackEvent(analyticEvents.performanceReviews.created, {
        [analyticProperties.id]: data.id,
      });
      navigate(`/performance-reviews/${data.id}/setup/${PerformanceReviewEditPageStep.details}`);
    } catch {}
  };

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.performanceReviews.viewed);
  });

  return (
    <Page className="h-full">
      <Page.Header title={t("performance_review:performance_review_cycles")}>
        {canCreateReview && (
          <Button
            isLoading={createReview.isPending}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            onClick={handleReviewCreate}
          >
            {t("performance_review:new_review_cycle")}
          </Button>
        )}
      </Page.Header>
      <Page.Content stretch>
        <Page.StickyLayout
          toolbar={
            <>
              <CreationSourceTabs
                value={creationSource ?? undefined}
                onChange={handleCreationSourceChange}
                sources={[ApiCreationSource.manual, ApiCreationSource.workflow, undefined]}
              />
              <Box className={"mt-2"}>
                <Search
                  value={searchTerm}
                  onInput={(e) => {
                    setSearchTerm(e.currentTarget.value);
                  }}
                  type={"text"}
                  placeholder={t("performance_review:review_cycle")}
                  className={"w-96"}
                />
              </Box>
            </>
          }
          footer={
            <PaginationBlock
              metadata={reviews?.metadata}
              onPageChange={(page) => {
                setSearchParams((searchParams) => {
                  searchParams.set("page", page.toString());
                  return searchParams;
                });
              }}
            />
          }
        >
          <Table
            stickyHeader={true}
            renderRowAction={renderRowAction}
            onRowClick={(row) => navigate(`/performance-reviews/${row.original.id}/view/summary`)}
            data={reviews?.items || []}
            columns={columns}
            showSkeleton={isLoading}
            isFetching={isLoading || isFetching}
            showNotFoundError={
              (filter.text != undefined || creationSource === ApiCreationSource.workflow) &&
              reviews?.metadata.total_items == 0
            }
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={
              <EmptyStateBase
                emoji={emojiDefaults.performanceReview.default}
                title={t(
                  canCreateReview
                    ? "performance_review:performance_reviews_empty_state_title"
                    : "performance_review:performance_reviews_empty_state_viewer_title"
                )}
                description={t(
                  canCreateReview
                    ? "performance_review:performance_reviews_empty_state_description"
                    : "performance_review:performance_reviews_empty_state_viewer_description"
                )}
                primaryButton={
                  canCreateReview && (
                    <Button
                      isLoading={createReview.isPending}
                      leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
                      onClick={handleReviewCreate}
                    >
                      {t("performance_review:create_performance_review")}
                    </Button>
                  )
                }
              />
            }
          />
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
};
